import { Panel, CountKpi } from '../components/CommandCentreParts.js';
import { EmptyState } from '../components/NoData.js';
import { useApi, type GetToken } from '../lib/useApi.js';
import { gbp, percent, faintIfNull, statusPill, NO_DATA } from '../lib/format.js';

interface BudgetCampaign {
  name: string; status: string;
  dailyBudgetGbp: number | null; monthlyBudgetGbp: number | null;
  spendThisMonthGbp: number | null; expectedByTodayGbp: number | null;
  hasServed: boolean; pacePercent: number | null;
}

interface BudgetResponse {
  month: string; dayOfMonth: number; daysInMonth: number;
  totalMonthlyBudgetGbp: number;
  totalSpendThisMonthGbp: number | null;
  campaigns: BudgetCampaign[];
  generatedUtc: string;
}

/** Pacing colour is only meaningful once something has served. */
function paceColour(pace: number | null): string | undefined {
  if (pace === null) return undefined;
  if (pace > 115) return 'var(--negative)';
  if (pace < 85) return 'var(--warning)';
  return 'var(--positive)';
}

export function Budget({ getToken }: { getToken: GetToken }): JSX.Element {
  const { data, error } = useApi<BudgetResponse>('/api/budget', getToken);

  if (error) return <EmptyState title="Could not load budget" tag="Error">{error}</EmptyState>;
  if (!data) return <EmptyState title="Loading…" tag="Please wait">Reading budgets and month-to-date spend.</EmptyState>;

  const enabled = data.campaigns.filter((c) => c.status === 'ENABLED');
  const monthElapsed = Math.round((data.dayOfMonth / data.daysInMonth) * 1000) / 10;
  const remaining = data.totalSpendThisMonthGbp === null
    ? null : Math.round((data.totalMonthlyBudgetGbp - data.totalSpendThisMonthGbp) * 100) / 100;

  return (
    <div className="stack">
      {data.totalSpendThisMonthGbp === null ? (
        <div className="panel" style={{ borderColor: 'var(--warning)' }}>
          <h2>No spend has occurred</h2>
          <p>
            Every campaign is paused, so month-to-date spend reads <strong>{NO_DATA}</strong>. The budgets
            below are configured in Google Ads and are what <em>would</em> be spent were the campaigns
            enabled — they are a commitment, not a measurement, and nothing has yet been charged.
          </p>
        </div>
      ) : null}

      <Panel
        title={`Budget — ${data.month}`}
        subtitle={`Day ${data.dayOfMonth} of ${data.daysInMonth}. ${percent(monthElapsed)} of the month has elapsed.`}
      >
        <div className="kpi-grid">
          <CountKpi
            label="Committed monthly budget"
            value={data.totalMonthlyBudgetGbp > 0 ? data.totalMonthlyBudgetGbp : null}
            money
            note={`${enabled.length} of ${data.campaigns.length} campaigns enabled`}
          />
          <CountKpi label="Spend month to date" value={data.totalSpendThisMonthGbp} money note="Actual charges" />
          <CountKpi label="Budget remaining" value={remaining} money note="Committed less spent" />
          <CountKpi
            label="Month elapsed"
            value={monthElapsed}
            note="Even-delivery reference point"
          />
        </div>
      </Panel>

      <Panel
        title="Pacing by campaign"
        subtitle="Expected-by-today assumes even delivery across the month. Pace above 100% means spending faster than that line."
      >
        <table className="data">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Status</th>
              <th className="num">Daily budget</th>
              <th className="num">Monthly budget</th>
              <th className="num">Expected by today</th>
              <th className="num">Spent this month</th>
              <th className="num">Pace</th>
            </tr>
          </thead>
          <tbody>
            {data.campaigns.map((c) => (
              <tr key={c.name}>
                <td>{c.name}</td>
                <td><span className={statusPill(c.status)}>{c.status}</span></td>
                <td className="num" style={faintIfNull(c.dailyBudgetGbp)}>{gbp(c.dailyBudgetGbp)}</td>
                <td className="num" style={faintIfNull(c.monthlyBudgetGbp)}>{gbp(c.monthlyBudgetGbp)}</td>
                <td className="num" style={{ color: 'var(--text-muted)' }}>{gbp(c.expectedByTodayGbp)}</td>
                <td className="num" style={faintIfNull(c.spendThisMonthGbp)}>{gbp(c.spendThisMonthGbp)}</td>
                <td className="num" style={{ color: paceColour(c.pacePercent) ?? 'var(--text-faint)' }}>
                  {percent(c.pacePercent)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ marginTop: 14, fontSize: 12, color: 'var(--text-faint)' }}>
          Monthly budget is the Google Ads daily budget × 30.4, which is how Google itself derives a monthly
          figure. Expected-by-today is shown for every campaign because it is arithmetic on the budget;
          pace is {NO_DATA} until there is spend to compare it against.
        </p>
      </Panel>
    </div>
  );
}
